import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { DragDropContext } from '@hello-pangea/dnd'
import { useAuth } from '../../contexts/AuthContext'
import KanbanColumn from '../../components/kanban/KanbanColumn'
import MobileKanbanView from '../../components/kanban/MobileKanbanView'
import ProofUploadModal from '../../components/modals/ProofUploadModal'
import FacilityAssignmentModal from '../../components/modals/FacilityAssignmentModal'
import GymDetailsModal from '../../components/modals/GymDetailsModal'
import LogVisitModal from '../../components/modals/LogVisitModal'
import SiteVisitModal from '../../components/modals/SiteVisitModal'
import ProposalSentModal from '../../components/modals/ProposalSentModal'
import InternalApprovalModal from '../../components/modals/InternalApprovalModal'
import AgreementDocumentsModal from '../../components/modals/AgreementDocumentsModal'
import RemoveGymModal from '../../components/modals/RemoveGymModal'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import axios from 'axios'
import { toast } from 'react-toastify'

const STAGES = [
  { id: 'New Lead', title: 'New Lead', color: 'gray', icon: '1' },
  { id: 'Site Visit', title: 'Site Visit', color: 'blue', icon: '2' },
  { id: 'Internal Approval', title: 'Internal Approval', color: 'orange', icon: '3' },
  { id: 'CEO Approval', title: 'CEO Approval', color: 'pink', icon: '4' },
  { id: 'Proposal Sent', title: 'Proposal Sent', color: 'yellow', icon: '5' },
  { id: 'Pre-Work Checklist', title: 'Pre-Work Checklist', color: 'purple', icon: '6' },
  { id: 'Agreement Signed', title: 'Agreement Signed', color: 'green', icon: '7' },
  { id: 'Handed to Activation', title: 'Handed to Activation', color: 'red', icon: '8' }
]

const KanbanBoard = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [allGyms, setAllGyms] = useState([])
  const [loading, setLoading] = useState(true)
  const [dragLoading, setDragLoading] = useState(false)
  const [viewMode, setViewMode] = useState('auto') // 'auto', 'cards', 'board'
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [filters, setFilters] = useState({
    gymName: '',
    city: ''
  })

  // Handle window resize for responsive view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  const [proofModal, setProofModal] = useState({
    isOpen: false,
    gym: null,
    fromStage: '',
    toStage: ''
  })
  const [facilityAssignmentModal, setFacilityAssignmentModal] = useState({
    isOpen: false,
    gym: null,
    fromStage: '',
    toStage: ''
  })
  const [detailsModal, setDetailsModal] = useState({
    isOpen: false,
    gymId: null
  })
  const [logVisitModal, setLogVisitModal] = useState({
    isOpen: false,
    gym: null
  })
  const [siteVisitModal, setSiteVisitModal] = useState({
    isOpen: false,
    gym: null,
    fromStage: '',
    toStage: ''
  })
  const [proposalSentModal, setProposalSentModal] = useState({
    isOpen: false,
    gym: null
  })
  const [internalApprovalModal, setInternalApprovalModal] = useState({
    isOpen: false,
    gym: null,
    initialAction: null // 'approve' | 'reject' | null
  })
  const [agreementDocumentsModal, setAgreementDocumentsModal] = useState({
    isOpen: false,
    gym: null
  })
  const [removeGymModal, setRemoveGymModal] = useState({
    isOpen: false,
    gym: null
  })

  // Role-based stage restrictions
  const BD_EXECUTIVE_ALLOWED_STAGES = ['New Lead', 'Site Visit', 'Internal Approval']
  const HEAD_OF_ACQUISITION_ALLOWED_STAGES = ['Site Visit', 'Internal Approval', 'CEO Approval', 'Proposal Sent', 'Pre-Work Checklist', 'Agreement Signed']

  const isBDExecutive = user?.role?.level === 4
  const isHeadOfAcquisition = user?.role?.level === 3
  const isCOO = user?.role?.level === 2
  const isCEO = user?.role?.level === 1

  // Load all gyms once on mount
  useEffect(() => {
    fetchGyms()
  }, [])

  // Keyboard shortcut to clear filters
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && (filters.gymName || filters.city)) {
        setFilters({ gymName: '', city: '' })
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [filters.gymName, filters.city])

  const fetchGyms = async () => {
    try {
      setLoading(true)
      // Fetch all gyms without pagination limit for Kanban view
      const response = await axios.get('/api/gyms', {
        params: { limit: 1000 }
      })
      setAllGyms(response.data.gyms)
    } catch (error) {
      console.error('Error fetching gyms:', error)
      toast.error('Failed to load gyms')
    } finally {
      setLoading(false)
    }
  }

  // Client-side filtering
  const gyms = allGyms.filter(gym => {
    const matchesName = !filters.gymName ||
      gym.name.toLowerCase().includes(filters.gymName.toLowerCase())
    const matchesCity = !filters.city ||
      gym.location?.toLowerCase().includes(filters.city.toLowerCase())
    return matchesName && matchesCity
  })

  const canUserMoveGym = (gym, targetStage) => {
    // Check for backward movement - prevent moving to previous stages
    const stages = STAGES.map(s => s.id)
    const currentIndex = stages.indexOf(gym.stage)
    const destinationIndex = stages.indexOf(targetStage)

    if (currentIndex !== -1 && destinationIndex !== -1) {
      // Forward jumps are still capped at 2 stages so users don't accidentally
      // skip approvals. Backward moves are now allowed (audited via stage history).
      if (destinationIndex - currentIndex > 2) {
        return {
          allowed: false,
          reason: 'Cannot skip more than 2 stages at once. Please move through stages sequentially.'
        }
      }
    }

    // BD Executive restrictions
    if (isBDExecutive) {
      if (!BD_EXECUTIVE_ALLOWED_STAGES.includes(targetStage)) {
        return {
          allowed: false,
          reason: 'BD Executive can only move gyms up to Internal Approval stage',
          isRestrictedStage: true
        }
      }
      if (gym.assignedUserId !== user.id) {
        return {
          allowed: false,
          reason: 'You can only move gyms assigned to you',
          isRestrictedStage: false
        }
      }
    }

    // Head of Acquisition restrictions
    if (isHeadOfAcquisition) {
      if (!HEAD_OF_ACQUISITION_ALLOWED_STAGES.includes(targetStage)) {
        return {
          allowed: false,
          reason: 'Head of Acquisition can only move gyms up to Agreement Signed stage',
          isRestrictedStage: true
        }
      }
    }


    // New Lead to Site Visit - allow direct move without proof
    // BD can move gym to Site Visit on first visit, then collect details over up to 3 visits
    if (gym.stage === 'New Lead' && targetStage === 'Site Visit') {
      return {
        allowed: true,
        isDirectMove: true, // Flag to indicate this is a direct move without proof
        reason: 'Moving to Site Visit stage'
      }
    }

    // Check for proof requirements for other transitions
    const proofRequiredTransitions = [
      { from: 'Site Visit', to: 'Proposal Sent' },
      { from: 'Proposal Sent', to: 'Agreement Signed' }
    ]

    const requiresProof = proofRequiredTransitions.some(
      transition => transition.from === gym.stage && transition.to === targetStage
    )

    if (requiresProof) {
      return {
        allowed: true,
        isProofRequired: true,
        reason: 'Proof of work required for this transition'
      }
    }

    return { allowed: true }
  }

  const handleDragStart = (start) => {
    console.log('Drag started:', start)
  }

  const handleDragEnd = async (result) => {
    console.log('Drag ended:', result)
    if (!result.destination) return

    const { source, destination, draggableId } = result

    if (source.droppableId === destination.droppableId) return

    // Use allGyms instead of filtered gyms to ensure we find the gym even if filters are active
    const gym = allGyms.find(g => g.id === draggableId)
    if (!gym) {
      console.error('Gym not found with id:', draggableId)
      toast.error('Gym not found')
      return
    }

    // Check permissions
    const moveCheck = canUserMoveGym(gym, destination.droppableId)

    if (!moveCheck.allowed) {
      if (moveCheck.isProofRequired) {
        // Show more prominent warning for proof requirement
        toast.warning(moveCheck.reason, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
      } else if (moveCheck.isBackwardMove) {
        // Show specific message for backward movement attempt
        toast.error(moveCheck.reason, {
          position: "top-center",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
      } else if (moveCheck.isRestrictedStage) {
        // Show specific message for Head of Acquisition restricted stages
        toast.error(moveCheck.reason, {
          position: "top-center",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
      } else {
        // Show general error for other permission issues
        toast.error(moveCheck.reason, {
          position: "top-center",
          autoClose: 3000,
        })
      }
      return
    }

    // Define stage transitions that require proof of work
    const proofRequiredTransitions = [
      // Proposal Sent → Agreement Signed now uses InternalApprovalModal
    ]

    // Check if this transition requires proof
    const requiresProof = proofRequiredTransitions.some(
      transition => transition.from === source.droppableId && transition.to === destination.droppableId
    )

    // Check if this transition is from Proposal Sent to Agreement Signed (requires internal approval)
    const isMovingToAgreementSigned = source.droppableId === 'Proposal Sent' && destination.droppableId === 'Agreement Signed'

    // Check if this transition is from New Lead to Site Visit (requires site visit details)
    const isMovingToSiteVisit = source.droppableId === 'New Lead' && destination.droppableId === 'Site Visit'

    // Check if this transition is from Site Visit to Internal Approval (direct move)
    const isMovingToInternalApproval = source.droppableId === 'Site Visit' && destination.droppableId === 'Internal Approval'

    // Check if this transition is from Internal Approval to CEO Approval (HoA/COO approval)
    const isMovingFromInternalApprovalToCEOApproval = source.droppableId === 'Internal Approval' && destination.droppableId === 'CEO Approval'

    // Check if this transition is from CEO Approval to Proposal Sent (CEO approval required)
    const isMovingFromCEOApprovalToProposalSent = source.droppableId === 'CEO Approval' && destination.droppableId === 'Proposal Sent'

    // Check if this transition is from Site Visit to Proposal Sent (requires email) - now goes through Internal Approval
    const isMovingToProposalSent = source.droppableId === 'Site Visit' && destination.droppableId === 'Proposal Sent'


    if (isMovingToSiteVisit) {
      // Direct move from New Lead to Site Visit - no modal required
      // BD can collect details over up to 3 visits using "Log Visit" button
      setDragLoading(true)
      try {
        const response = await axios.put(`/api/gyms/${gym.id}/stage`, {
          stage: destination.droppableId
        })

        // Update local state with new stage
        setAllGyms(prev => prev.map(g =>
          g.id === gym.id ? { ...g, stage: destination.droppableId } : g
        ))

        toast.success(`${gym.name} moved to Site Visit! Use "Log Visit" to record visit details.`, {
          position: "top-center",
          autoClose: 4000,
        })
      } catch (error) {
        console.error('Error moving gym:', error)
        toast.error(error.response?.data?.error || 'Failed to move gym')
      } finally {
        setDragLoading(false)
      }
    } else if (isMovingToInternalApproval) {
      // Direct move from Site Visit to Internal Approval - no modal required
      setDragLoading(true)
      try {
        const response = await axios.put(`/api/gyms/${gym.id}/stage`, {
          stage: destination.droppableId
        })

        // Update local state with new stage
        setAllGyms(prev => prev.map(g =>
          g.id === gym.id ? { ...g, stage: destination.droppableId } : g
        ))

        toast.success(`${gym.name} moved to Internal Approval. CEO can now review and approve.`, {
          position: "top-center",
          autoClose: 4000,
        })
      } catch (error) {
        console.error('Error moving gym:', error)
        toast.error(error.response?.data?.error || 'Failed to move gym')
      } finally {
        setDragLoading(false)
      }
    } else if (isMovingFromInternalApprovalToCEOApproval) {
      // HoA, COO, and CEO can move from Internal Approval to CEO Approval
      if (!isCEO && !isCOO && !isHeadOfAcquisition) {
        toast.error('Only Head of Acquisition, COO, or CEO can move gyms from Internal Approval to CEO Approval', {
          position: "top-center",
          autoClose: 4000,
        })
        return
      }
      // Show internal approval modal for HoA, COO, or CEO
      setInternalApprovalModal({
        isOpen: true,
        gym
      })
    } else if (isMovingFromCEOApprovalToProposalSent) {
      // Only CEO can move from CEO Approval to Proposal Sent
      if (!isCEO) {
        toast.error('Only the CEO can approve a gym from CEO Approval to Proposal Sent', {
          position: "top-center",
          autoClose: 4000,
        })
        return
      }
      // Show internal approval modal for CEO (final approval)
      setInternalApprovalModal({
        isOpen: true,
        gym
      })
    } else if (isMovingToAgreementSigned) {
      // Direct move from Proposal Sent to Agreement Signed - no modal required
      setDragLoading(true)
      try {
        const response = await axios.put(`/api/gyms/${gym.id}/stage`, {
          stage: destination.droppableId
        })

        // Update local state with new stage
        setAllGyms(prev => prev.map(g =>
          g.id === gym.id ? { ...g, stage: destination.droppableId } : g
        ))

        toast.success(`${gym.name} moved to Agreement Signed`, {
          position: "top-center",
          autoClose: 3000,
        })
      } catch (error) {
        console.error('Error moving gym:', error)
        toast.error(error.response?.data?.error || 'Failed to move gym')
      } finally {
        setDragLoading(false)
      }
    } else if (isMovingToProposalSent) {
      // Show proposal sent modal with email preview
      setProposalSentModal({
        isOpen: true,
        gym
      })
    } else if (requiresProof) {
      // Show proof upload modal for required transitions
      setProofModal({
        isOpen: true,
        gym,
        fromStage: source.droppableId,
        toStage: destination.droppableId
      })
    } else {
      // For other transitions, move directly without proof requirement
      try {
        setDragLoading(true)
        const response = await axios.put(`/api/gyms/${gym.id}`, {
          stage: destination.droppableId
        })
        
        // Update the gym in the local state
        setAllGyms(prev => prev.map(g => 
          g.id === gym.id ? { ...g, stage: destination.droppableId } : g
        ))
        
        toast.success(`${gym.name} moved to ${destination.droppableId}`)
      } catch (error) {
        console.error('Error moving gym:', error)
        const errorMessage = error.response?.data?.error || 'Failed to move gym'
        toast.error(errorMessage, {
          autoClose: 5000,
          position: "top-center"
        })
      } finally {
        setDragLoading(false)
      }
    }
  }

  const getGymsForStage = (stage) => {
    return gyms.filter(gym => gym.stage === stage)
  }

  const getVisibleStages = () => {
    // All roles see the same 7 stages; movement restrictions are handled at drag/drop level
    return STAGES
  }

  const handleProofUploadSuccess = (data) => {
    // Update the gym in the local state
    setAllGyms(prev => prev.map(g => 
      g.id === data.gym.id ? data.gym : g
    ))
    
    // Close the modal
    setProofModal({
      isOpen: false,
      gym: null,
      fromStage: '',
      toStage: ''
    })
  }

  const handleProofModalClose = () => {
    setProofModal({
      isOpen: false,
      gym: null,
      fromStage: '',
      toStage: ''
    })
  }

  const handleFacilityAssignmentSuccess = (data) => {
    // Update the gym in the local state
    setAllGyms(prev => prev.map(g => 
      g.id === data.gym.id ? data.gym : g
    ))
    
    // Close the modal
    setFacilityAssignmentModal({
      isOpen: false,
      gym: null,
      fromStage: '',
      toStage: ''
    })

    // Show success message
    toast.success(
      `${data.gym.name} moved to Pre-Work Checklist with ${data.tasksAssigned} tasks assigned to ${data.assignedTo}`,
      {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      }
    )
  }

  const handleFacilityAssignmentModalClose = () => {
    setFacilityAssignmentModal({
      isOpen: false,
      gym: null,
      fromStage: '',
      toStage: ''
    })
  }

  const handleSiteVisitSuccess = (data) => {
    // Handle gym deletion (expired due to too many visits)
    if (data.gymDeleted) {
      setAllGyms(prev => prev.filter(g => g.id !== data.expiredClient?.originalGymId && g.id !== data.deletedGym?.id))

      if (data.expiredClient) {
        toast.success(`Gym "${data.expiredClient.name}" moved to expired clients due to visit limit exceeded`, {
          position: "top-center",
          autoClose: 4000
        })
      }
    } else if (data.gym) {
      // Update the gym in the local state
      setAllGyms(prev => prev.map(g =>
        g.id === data.gym.id ? data.gym : g
      ))

      // Show success message
      toast.success(`Site visit completed for ${data.gym.name}!`, {
        position: "top-center",
        autoClose: 4000
      })
    }

    // Close the modal
    setSiteVisitModal({
      isOpen: false,
      gym: null,
      fromStage: '',
      toStage: ''
    })
  }

  const handleSiteVisitModalClose = () => {
    setSiteVisitModal({
      isOpen: false,
      gym: null,
      fromStage: '',
      toStage: ''
    })
  }

  const handleProposalSentSuccess = (data) => {
    // Update the gym in the local state
    setAllGyms(prev => prev.map(g =>
      g.id === data.gym.id ? data.gym : g
    ))

    // Close the modal
    setProposalSentModal({
      isOpen: false,
      gym: null
    })

    // Show success message
    toast.success(`Proposal sent to ${data.gym.ownerEmail || 'gym owner'}!`, {
      position: "top-center",
      autoClose: 4000
    })
  }

  const handleProposalSentModalClose = () => {
    setProposalSentModal({
      isOpen: false,
      gym: null
    })
  }

  const handleInternalApprovalSuccess = (data) => {
    // Update the gym in the local state
    setAllGyms(prev => prev.map(g =>
      g.id === data.gym.id ? data.gym : g
    ))

    // Close the internal approval modal
    setInternalApprovalModal({
      isOpen: false,
      gym: null
    })

    // If gym moved to Proposal Sent, open ProposalSentModal for membership plans + email
    if (data.gym.stage === 'Proposal Sent') {
      toast.success(`${data.gym.name} approved! Now fill in proposal details & membership plans.`, {
        position: "top-center",
        autoClose: 4000
      })
      // Open the Proposal Sent modal so user can add membership plans + send email
      setTimeout(() => {
        setProposalSentModal({
          isOpen: true,
          gym: data.gym
        })
      }, 500)
    } else {
      toast.success(`${data.gym.name} approved and moved to ${data.gym.stage}!`, {
        position: "top-center",
        autoClose: 4000
      })
    }
  }

  const handleInternalApprovalRejected = (data) => {
    // Remove the rejected gym from the local state
    setAllGyms(prev => prev.filter(g => g.id !== data.originalGymId))

    // Close the modal
    setInternalApprovalModal({
      isOpen: false,
      gym: null
    })

    // Show message about rejection
    toast.info(`${data.expiredClient?.name} has been moved to Expired Clients`, {
      position: "top-center",
      autoClose: 4000
    })
  }

  const handleInternalApprovalModalClose = () => {
    setInternalApprovalModal({
      isOpen: false,
      gym: null,
      initialAction: null
    })
  }

  const handleUploadDocuments = (gym) => {
    setAgreementDocumentsModal({
      isOpen: true,
      gym
    })
  }

  const handleAgreementDocumentsSuccess = (data) => {
    // Update the gym in the local state (if any gym data returned)
    if (data.gym) {
      setAllGyms(prev => prev.map(g =>
        g.id === data.gym.id ? data.gym : g
      ))
    }

    // Close the modal
    setAgreementDocumentsModal({
      isOpen: false,
      gym: null
    })

    // Show success message
    toast.success(`${data.documentsUploaded || 'Documents'} uploaded successfully!`, {
      position: "top-center",
      autoClose: 4000
    })
  }

  const handleAgreementDocumentsModalClose = () => {
    setAgreementDocumentsModal({
      isOpen: false,
      gym: null
    })
  }

  const handlePreRecce = (gym) => {
    navigate(`/gyms/${gym.id}/pre-recce`)
  }

  const handleViewDetails = (gym) => {
    setDetailsModal({
      isOpen: true,
      gymId: gym.id
    })
  }

  const handleDetailsModalClose = () => {
    setDetailsModal({
      isOpen: false,
      gymId: null
    })
  }

  const handleLogVisit = (gym) => {
    // Use the full-featured SiteVisitModal instead of simple LogVisitModal
    setSiteVisitModal({
      isOpen: true,
      gym,
      fromStage: gym.stage,
      toStage: 'Site Visit'
    })
  }

  // Handle stage move from mobile view
  const handleMobileStageMove = async (gym, targetStage) => {
    // Ensure we have a valid gym object with id
    if (!gym || !gym.id) {
      toast.error('Invalid gym selected')
      return
    }

    // Find the gym from allGyms to ensure we have the full object
    const fullGym = allGyms.find(g => g.id === gym.id)
    if (!fullGym) {
      toast.error('Gym not found')
      return
    }

    // Simulate a drag result for reusing the same logic
    const simulatedResult = {
      destination: { droppableId: targetStage },
      source: { droppableId: fullGym.stage },
      draggableId: fullGym.id
    }
    await handleDragEnd(simulatedResult)
  }

  const handleLogVisitModalClose = () => {
    setLogVisitModal({
      isOpen: false,
      gym: null
    })
  }

  const handleLogVisitSuccess = (data) => {
    if (data.gymDeleted) {
      // Remove the deleted gym from the local state
      setAllGyms(prev => prev.filter(g => g.id !== data.expiredClient?.originalGymId || g.id !== data.deletedGym?.id))
      
      // Show success message for expired client
      if (data.expiredClient) {
        toast.success(`Gym "${data.expiredClient.name}" moved to expired clients due to visit limit exceeded`)
      } else {
        toast.success(`Gym "${data.deletedGym?.name}" moved to expired clients`)
      }
      
      // Close the modal
      setLogVisitModal({
        isOpen: false,
        gym: null
      })
    } else {
      // Update the gym in the local state
      setAllGyms(prev => prev.map(g => 
        g.id === data.gym.id ? data.gym : g
      ))
      
      // Close the modal
      setLogVisitModal({
        isOpen: false,
        gym: null
      })
    }
  }

  // Handle remove gym
  const handleRemoveGym = (gym) => {
    setRemoveGymModal({
      isOpen: true,
      gym
    })
  }

  const handleRemoveGymModalClose = () => {
    setRemoveGymModal({
      isOpen: false,
      gym: null
    })
  }

  const handleRemoveGymSuccess = (data) => {
    // Remove the gym from local state
    setAllGyms(prev => prev.filter(g => g.id !== data.expiredClient?.originalGymId && g.id !== removeGymModal.gym?.id))

    // Close the modal
    setRemoveGymModal({
      isOpen: false,
      gym: null
    })
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Determine which view to show
  const showMobileView = viewMode === 'cards' || (viewMode === 'auto' && isMobile)

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kanban Board</h1>
          <p className="mt-1 text-sm text-gray-600 hidden md:block">
            Drag and drop gym cards across acquisition stages
            {isBDExecutive && (
              <span className="ml-2 text-yellow-600">
                (BD Executive view - can move cards up to Internal Approval stage)
              </span>
            )}
            {isHeadOfAcquisition && (
              <span className="ml-2 text-blue-600">
                (Head of Acquisition view - can approve up to Agreement Signed stage)
              </span>
            )}
            {isCOO && (
              <span className="ml-2 text-purple-600">
                (COO view - full pipeline access)
              </span>
            )}
            {isCEO && (
              <span className="ml-2 text-green-600">
                (CEO view - full pipeline access including Handed to Activation)
              </span>
            )}
          </p>
          {(filters.gymName || filters.city) && (
            <p className="mt-1 text-sm text-blue-600">
              Showing {gyms.length} gym{gyms.length !== 1 ? 's' : ''}
              {filters.gymName && ` matching "${filters.gymName}"`}
              {filters.city && ` in "${filters.city}"`}
            </p>
          )}
        </div>

        <div className="flex items-center space-x-2 md:space-x-4 w-full md:w-auto">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                showMobileView
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Card View"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <span className="hidden sm:inline">Cards</span>
            </button>
            <button
              onClick={() => setViewMode('board')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                !showMobileView
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Board View"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              <span className="hidden sm:inline">Board</span>
            </button>
          </div>

          {/* Floating Label for Head of Acquisition - Hidden on Mobile */}
          {isHeadOfAcquisition && !showMobileView && (
            <div className="hidden lg:block bg-green-50 border border-green-200 rounded-lg px-4 py-2">
              <div className="flex items-center">
                <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium text-green-800">
                  You can move cards up to Agreement Signed stage
                </span>
              </div>
            </div>
          )}

          {dragLoading && (
            <div className="flex items-center text-blue-600">
              <LoadingSpinner size="sm" className="mr-2" />
              Updating...
            </div>
          )}

          <button
            onClick={() => {
              fetchGyms()
              toast.info('Refreshing board...')
            }}
            className="btn-secondary"
          >
            <svg className="w-4 h-4 md:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden md:inline">Refresh</span>
          </button>
        </div>
      </div>

            {/* Role-based Notice - Hidden on mobile card view */}
      {!showMobileView && (isBDExecutive || isHeadOfAcquisition || isCOO || isCEO) && (
        <div className={`${
          isBDExecutive ? 'bg-blue-50 border-blue-200' : 
          isHeadOfAcquisition ? 'bg-green-50 border-green-200' :
          isCOO ? 'bg-purple-50 border-purple-200' :
          'bg-emerald-50 border-emerald-200'
        } border rounded-lg p-4`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className={`w-5 h-5 ${
                isBDExecutive ? 'text-blue-400' : 
                isHeadOfAcquisition ? 'text-green-400' :
                isCOO ? 'text-purple-400' :
                'text-emerald-400'
              }`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${
                isBDExecutive ? 'text-blue-800' : 
                isHeadOfAcquisition ? 'text-green-800' :
                isCOO ? 'text-purple-800' :
                'text-emerald-800'
              }`}>
                {isBDExecutive ? 'BD Executive Permissions' : 
                 isHeadOfAcquisition ? 'Head of Acquisition Permissions' :
                 isCOO ? 'COO Permissions' :
                 'CEO Permissions'}
              </h3>
              <div className={`mt-2 text-sm ${
                isBDExecutive ? 'text-blue-700' : 
                isHeadOfAcquisition ? 'text-green-700' :
                isCOO ? 'text-purple-700' :
                'text-emerald-700'
              }`}>
                {isBDExecutive ? (
                  <p>
                    You can view all stages and move gyms assigned to you from <strong>New Lead</strong> to <strong>Internal Approval</strong>.
                    Move gyms to Internal Approval after site visit for CEO review.
                  </p>
                ) : isHeadOfAcquisition ? (
                  <div>
                    <p>You can approve gym stage transitions through the following sequence:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li><strong>Site Visit → Internal Approval</strong></li>
                      <li><strong>Internal Approval → Proposal Sent</strong></li>
                      <li><strong>Proposal Sent → Pre-Work Checklist</strong></li>
                      <li><strong>Pre-Work Checklist → Agreement Signed</strong></li>
                    </ul>
                  </div>
                ) : isCOO ? (
                  <div>
                    <p>You have oversight of the full acquisition pipeline.</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>Can view and manage gyms in all stages</li>
                      <li>Can approve stage transitions up to <strong>Handed to Activation</strong></li>
                    </ul>
                  </div>
                ) : (
                  <div>
                    <p>You have full access to the entire acquisition pipeline including <strong>Handed to Activation</strong> stage.</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>Can view and manage gyms in all stages</li>
                      <li>Can approve final <strong>Handed to Activation</strong> stage transitions</li>
                      <li>Has ultimate authority over gym acquisition completion</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters - Hidden on mobile card view since it has its own filtering */}
      {!showMobileView && (
        <div className="card">
          <div className="card-body py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by City
                </label>
                <input
                  type="text"
                  value={filters.city}
                  onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
                  className="input"
                  placeholder="Enter city name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Gym Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={filters.gymName}
                    onChange={(e) => setFilters(prev => ({ ...prev, gymName: e.target.value }))}
                    className="input pl-10"
                    placeholder="Enter gym name to search"
                  />
                </div>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => setFilters({ gymName: '', city: '' })}
                  className="btn-secondary w-full"
                  title="Press Escape to clear filters"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      {!loading && (
        <>
          {showMobileView ? (
            /* Mobile Card View */
            <MobileKanbanView
              gyms={gyms}
              onViewDetails={handleViewDetails}
              onLogVisit={handleLogVisit}
              onUploadDocuments={handleUploadDocuments}
              onPreRecce={handlePreRecce}
              onMoveStage={handleMobileStageMove}
              onRemoveGym={handleRemoveGym}
              filters={filters}
              setFilters={setFilters}
              user={user}
              visibleStages={getVisibleStages()}
            />
          ) : (
            /* Desktop Board View */
            <>
              {/* Scroll hint for tablet */}
              <div className="xl:hidden mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center text-blue-700">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                  </svg>
                  <span className="text-sm">Scroll horizontally to see all stages</span>
                </div>
              </div>

              <DragDropContext
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <div className="kanban-board-container">
                  <div className="flex gap-4 lg:gap-6 min-h-[600px] pb-4" style={{ minWidth: `${getVisibleStages().length * 280}px` }}>
                    {getVisibleStages().map(stage => (
                      <div key={stage.id} className="flex-shrink-0 w-72 lg:w-80">
                        <KanbanColumn
                          column={stage}
                          gyms={getGymsForStage(stage.id)}
                          userRole={user?.role}
                          user={user}
                          isDropDisabled={dragLoading}
                          onViewDetails={handleViewDetails}
                          onLogVisit={handleLogVisit}
                          onUploadDocuments={handleUploadDocuments}
                          onPreRecce={handlePreRecce}
                          onRemoveGym={handleRemoveGym}
                          onInternalApprove={(gym) => setInternalApprovalModal({ isOpen: true, gym, initialAction: 'approve' })}
                          onInternalReject={(gym) => setInternalApprovalModal({ isOpen: true, gym, initialAction: 'reject' })}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </DragDropContext>
            </>
          )}
        </>
      )}

      {/* Summary - Hidden on mobile card view since it has its own summary */}
      {!showMobileView && (
        <div className="card">
          <div className="card-body">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {getVisibleStages().map(stage => {
                const count = getGymsForStage(stage.id).length
                return (
                  <div key={stage.id} className="text-center">
                    <div className="text-2xl font-bold text-wtf-red">{count}</div>
                    <div className="text-sm text-gray-600">{stage.title}</div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Proof Upload Modal */}
      <ProofUploadModal
        isOpen={proofModal.isOpen}
        onClose={handleProofModalClose}
        onSuccess={handleProofUploadSuccess}
        gym={proofModal.gym}
        fromStage={proofModal.fromStage}
        toStage={proofModal.toStage}
      />

      {/* Facility Assignment Modal */}
      <FacilityAssignmentModal
        isOpen={facilityAssignmentModal.isOpen}
        onClose={handleFacilityAssignmentModalClose}
        onSuccess={handleFacilityAssignmentSuccess}
        gym={facilityAssignmentModal.gym}
        fromStage={facilityAssignmentModal.fromStage}
        toStage={facilityAssignmentModal.toStage}
      />

      {/* Gym Details Modal */}
      <GymDetailsModal
        isOpen={detailsModal.isOpen}
        onClose={handleDetailsModalClose}
        gymId={detailsModal.gymId}
      />

      {/* Log Visit Modal */}
      <LogVisitModal
        isOpen={logVisitModal.isOpen}
        onClose={handleLogVisitModalClose}
        onSuccess={handleLogVisitSuccess}
        gym={logVisitModal.gym}
      />

      {/* Site Visit Modal */}
      <SiteVisitModal
        isOpen={siteVisitModal.isOpen}
        onClose={handleSiteVisitModalClose}
        onSuccess={handleSiteVisitSuccess}
        gym={siteVisitModal.gym}
        fromStage={siteVisitModal.fromStage}
        toStage={siteVisitModal.toStage}
      />

      {/* Proposal Sent Modal - Email Preview */}
      <ProposalSentModal
        isOpen={proposalSentModal.isOpen}
        onClose={handleProposalSentModalClose}
        onSuccess={handleProposalSentSuccess}
        gym={proposalSentModal.gym}
      />

      {/* Internal Approval Modal */}
      <InternalApprovalModal
        isOpen={internalApprovalModal.isOpen}
        onClose={handleInternalApprovalModalClose}
        onSuccess={handleInternalApprovalSuccess}
        onRejected={handleInternalApprovalRejected}
        gym={internalApprovalModal.gym}
        initialAction={internalApprovalModal.initialAction}
      />

      {/* Agreement Documents Modal */}
      <AgreementDocumentsModal
        isOpen={agreementDocumentsModal.isOpen}
        onClose={handleAgreementDocumentsModalClose}
        onSuccess={handleAgreementDocumentsSuccess}
        gym={agreementDocumentsModal.gym}
      />

      {/* Remove Gym Modal */}
      <RemoveGymModal
        isOpen={removeGymModal.isOpen}
        onClose={handleRemoveGymModalClose}
        onSuccess={handleRemoveGymSuccess}
        gym={removeGymModal.gym}
      />
    </div>
  )
}

export default KanbanBoard
